import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { supabase } from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';
import ReviewEditor from './ReviewEditor';
import { miniSaasProjects as staticProjects } from '../data/projects';
import { appCache } from '../lib/cache';

const { FiLogOut, FiBarChart2, FiActivity, FiMousePointer, FiMessageSquare, FiMail, FiCheck, FiSave, FiFilter, FiStar, FiPlus, FiEdit2, FiTrash2, FiEye, FiX, FiUpload, FiGlobe, FiTag, FiGrid, FiSettings, FiDownloadCloud, FiRefreshCw, FiTrash, FiAlertCircle, FiInbox, FiArrowLeft } = FiIcons;

// ── Shared Image Upload ────────────────────────────────────────────────────────
const ImageUploader = ({ value, onChange }) => {
  const [error, setError] = useState('');

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError('Only PNG, JPEG, or WEBP images are allowed.');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onloadend = () => {
      onChange(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex gap-4 items-start w-full">
      <label className="flex-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#2a1f42] hover:border-purple-500/50 rounded-xl p-6 cursor-pointer transition-all bg-[#0c0118] w-full">
        <SafeIcon icon={FiUpload} className="text-2xl text-white/30" />
        <span className="text-white/40 text-sm text-center">Click to upload image</span>
        <span className="text-white/20 text-xs">PNG, JPG, WEBP</span>
        <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleImage} className="hidden" />
      </label>
      {value && (
        <div className="relative w-24 h-24 sm:w-32 sm:h-24 rounded-xl overflow-hidden border border-[#2a1f42] flex-shrink-0">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange('')} className="absolute top-1 right-1 p-1 bg-black/70 rounded-full text-white/70 hover:text-white">
            <SafeIcon icon={FiX} className="text-xs" />
          </button>
        </div>
      )}
      {error && <p className="text-red-400 text-xs mt-1 absolute">{error}</p>}
    </div>
  );
};

// ── Review Form View (Page Type) ───────────────────────────────────────────────
const ReviewFormView = ({ review, onClose, onSave }) => {
  const isEdit = !!review && typeof review.id === 'string';
  const [form, setForm] = useState({
    title: review?.title || '',
    tool_name: review?.tool_name || '',
    affiliate_link: review?.affiliate_link || '',
    rating: review?.rating || 5,
    category: review?.category || '',
    tags: (review?.tags || []).join(', '),
    body: review?.body || '',
    status: review?.status || 'draft',
    image_url: review?.image_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        rating: parseFloat(form.rating),
        slug: isEdit ? review.slug : generateSlug(form.title),
        updated_at: new Date().toISOString(),
      };

      let err;
      if (isEdit) {
        ({ error: err } = await supabase.from('tool_reviews_1776536215359').update(payload).eq('id', review.id));
      } else {
        ({ error: err } = await supabase.from('tool_reviews_1776536215359').insert([payload]));
      }

      if (err) throw err;
      
      appCache.clearReviews();
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save review.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto bg-[#08000f] border border-[#2a1f42] rounded-2xl shadow-2xl overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent w-full" />
      <div className="flex items-center p-4 sm:p-6 border-b border-[#2a1f42] gap-4 bg-[#150a28]">
        <button type="button" onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
          <SafeIcon icon={FiArrowLeft} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Review' : 'New Review'}</h2>
          <p className="text-white/40 text-sm hidden sm:block">Fill in all details for the tool review</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Review Title *</label>
            <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Best AI Video Generator in 2025" className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Tool Name *</label>
            <div className="relative">
              <SafeIcon icon={FiGlobe} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
              <input required value={form.tool_name} onChange={e => setForm({...form, tool_name: e.target.value})} placeholder="Vidroid" className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 pl-9 pr-4 text-white text-sm focus:outline-none focus:border-purple-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Affiliate Link</label>
            <input value={form.affiliate_link} onChange={e => setForm({...form, affiliate_link: e.target.value})} placeholder="https://tool.com/?ref=fiverring" className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Rating (1–5)</label>
            <div className="flex items-center gap-3 bg-[#150a28] border border-[#2a1f42] rounded-xl px-4 py-2.5">
              <input type="range" min="1" max="5" step="0.5" value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} className="flex-1 accent-purple-500" />
              <span className="text-yellow-400 font-black text-lg w-8 text-center">{form.rating}</span>
              <SafeIcon icon={FiStar} className="text-yellow-400" style={{fill: '#facc15'}} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Category</label>
            <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="e.g. Video Generation" className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Tags (comma-separated)</label>
            <div className="relative">
              <SafeIcon icon={FiTag} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
              <input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="AI, Video, BYOK, Sora" className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 pl-9 pr-4 text-white text-sm focus:outline-none focus:border-purple-500" />
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Cover Image</label>
          <ImageUploader value={form.image_url} onChange={val => setForm({...form, image_url: val})} />
        </div>

        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Review Body *</label>
          <ReviewEditor value={form.body} onChange={body => setForm(f => ({...f, body}))} />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 border-t border-[#2a1f42]">
          <div className="w-full sm:w-auto">
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full sm:w-auto bg-[#150a28] border border-[#2a1f42] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          {error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 flex-1">{error}</p>}
          <div className="sm:ml-auto flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button type="button" onClick={onClose} className="w-full sm:w-auto px-5 py-2.5 bg-white/5 border border-white/10 text-white/60 font-bold rounded-xl hover:bg-white/10 transition-all text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="w-full sm:w-auto justify-center px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiSave} />}
              {saving ? 'Saving...' : (isEdit ? 'Update Review' : 'Publish Review')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// ── Reviews Tab (Review Management) ───────────────────────────────────────────
const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [fetchError, setFetchError] = useState(null);

  const fetchReviews = useCallback(async () => {
    setFetchError(null);
    try {
      const { data, error } = await supabase.from('tool_reviews_1776536215359').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setReviews(data || []);
    } catch(err) {
      console.error("Error fetching reviews:", err);
      setFetchError(err.message || "Failed to load reviews. Please check database connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const toggleSelect = (id) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) newSelection.delete(id);
    else newSelection.add(id);
    setSelectedIds(newSelection);
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedIds.size} reviews?`)) return;
    await supabase.from('tool_reviews_1776536215359').delete().in('id', Array.from(selectedIds));
    setSelectedIds(new Set());
    appCache.clearReviews();
    fetchReviews();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this review?')) return;
    await supabase.from('tool_reviews_1776536215359').delete().eq('id', id);
    appCache.clearReviews();
    fetchReviews();
  };

  if (showForm) {
    return (
      <div className="w-full">
        <ReviewFormView review={editingReview} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); fetchReviews(); }} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Tool Reviews</h2>
          <p className="text-white/40 text-sm">{reviews.length} total reviews</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 font-bold rounded-xl hover:bg-red-500/30 transition-all text-sm">
              <SafeIcon icon={FiTrash} /> Delete ({selectedIds.size})
            </button>
          )}
          <button onClick={() => { setEditingReview(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 transition-all text-sm">
            <SafeIcon icon={FiPlus} /> Add Review
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
          <SafeIcon icon={FiAlertCircle} className="text-red-400 mt-0.5" />
          <div>
            <h4 className="text-red-400 font-bold text-sm">Error Loading Reviews</h4>
            <p className="text-red-400/80 text-xs mt-1">{fetchError}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-20 text-white/30 border border-dashed border-[#392e4e] rounded-2xl">
          <SafeIcon icon={FiStar} className="text-4xl mx-auto mb-3 opacity-30" />
          <p>No reviews yet. Create your first review using the button above.</p>
        </div>
      ) : (
        <div className="bg-[#0c0518] border border-[#392e4e] rounded-xl w-full overflow-hidden shadow-sm">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left whitespace-nowrap min-w-max">
              <thead>
                <tr className="border-b border-white/5 bg-white/5 text-white/40 text-xs uppercase tracking-wider">
                  <th className="p-4 w-10">
                    <input type="checkbox" onChange={(e) => setSelectedIds(e.target.checked ? new Set(reviews.map(t => t.id)) : new Set())} checked={selectedIds.size === reviews.length && reviews.length > 0} className="w-4 h-4 accent-purple-500 rounded" />
                  </th>
                  <th className="p-4 font-medium">Review Title</th>
                  <th className="p-4 font-medium">Tool</th>
                  <th className="p-4 font-medium">Rating</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white text-sm divide-y divide-white/5">
                {reviews.map(r => (
                  <tr key={r.id} className={`hover:bg-white/5 transition-colors ${selectedIds.has(r.id) ? 'bg-purple-500/10' : ''}`}>
                    <td className="p-4">
                      <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} className="w-4 h-4 accent-purple-500 rounded" />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {r.image_url ? (
                          <img src={r.image_url} alt={r.title} className="w-8 h-8 rounded-md object-cover border border-[#392e4e] flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-xs font-bold flex-shrink-0 text-white">
                            {r.title.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-bold">{r.title}</div>
                          <div className="text-xs text-white/40">{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-white/60">{r.tool_name}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-yellow-400">
                        <SafeIcon icon={FiStar} style={{fill: '#facc15'}} />
                        <span className="font-bold">{r.rating}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      {r.status === 'published' ? (
                        <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-bold uppercase">Published</span>
                      ) : (
                        <span className="px-2 py-1 rounded bg-white/10 text-white/50 text-xs font-bold uppercase">Draft</span>
                      )}
                    </td>
                    <td className="p-4 flex gap-2 justify-end">
                      <button onClick={() => { setEditingReview(r); setShowForm(true); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                        <SafeIcon icon={FiEdit2} className="text-sm" />
                      </button>
                      <button onClick={() => handleDelete(r.id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400/60 hover:text-red-400 transition-all">
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tool Form View (Page Type) ─────────────────────────────────────────────────
const ToolFormView = ({ tool, onClose, onSave }) => {
  const isEdit = !!tool && typeof tool.id === 'string';
  const [form, setForm] = useState({
    name: tool?.name || '',
    description: tool?.description || '',
    url: tool?.url || '',
    category: tool?.category || 'productivity',
    tags: (tool?.tags || []).join(', '),
    featured: tool?.featured || false,
    image_url: tool?.image_url || '',
    status: tool?.status || 'approved',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        dateAdded: isEdit ? tool.dateAdded : new Date().toISOString(),
      };

      let err;
      if (isEdit) {
        ({ error: err } = await supabase.from('saas_projects_1776800000001').update(payload).eq('id', tool.id));
      } else {
        ({ error: err } = await supabase.from('saas_projects_1776800000001').insert([payload]));
      }

      if (err) throw err;
      
      appCache.clearTools();
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save tool.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-[#08000f] border border-[#2a1f42] rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center p-4 sm:p-6 border-b border-[#2a1f42] gap-4 bg-[#150a28]">
        <button type="button" onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
          <SafeIcon icon={FiArrowLeft} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Tool' : 'Add New Tool'}</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Tool Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">URL *</label>
            <input required type="url" value={form.url} onChange={e => setForm({...form, url: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Description *</label>
          <textarea required rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500 resize-none" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Category</label>
            <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full bg-[#150a28] border border-[#2a1f42] rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500">
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Featured Image</label>
            <ImageUploader value={form.image_url} onChange={val => setForm({...form, image_url: val})} />
          </div>
        </div>

        <label className="flex items-center gap-3 p-4 bg-[#150a28] border border-[#2a1f42] rounded-xl cursor-pointer">
          <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="w-5 h-5 accent-purple-500 rounded bg-[#0c0518] border-[#392e4e] flex-shrink-0" />
          <span className="text-sm text-white font-medium">Mark as Featured (Highlights tool on homepage)</span>
        </label>

        {(tool?.submitter_name || tool?.submitter_email) && (
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
            <h4 className="text-xs font-bold text-purple-400 uppercase mb-2">Submitter Information</h4>
            <p className="text-sm text-white/80"><strong>Name:</strong> {tool.submitter_name || 'N/A'}</p>
            <p className="text-sm text-white/80"><strong>Email:</strong> {tool.submitter_email || 'N/A'}</p>
          </div>
        )}

        {error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-[#2a1f42]">
          <button type="button" onClick={onClose} className="w-full sm:w-auto px-5 py-2.5 bg-white/5 text-white/60 font-bold rounded-xl hover:bg-white/10 transition-all text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="w-full sm:w-auto px-6 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 transition-all disabled:opacity-50 text-sm">
            {saving ? 'Saving...' : 'Save Tool'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Submissions Tab (Pending Tools) ───────────────────────────────────────────
const SubmissionsTab = ({ onCountChange }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('saas_projects_1776800000001')
        .select('*')
        .eq('status', 'pending')
        .order('dateAdded', { ascending: false });
      
      if (error) throw error;
      setSubmissions(data || []);
      if (onCountChange) onCountChange(data?.length || 0);
    } catch(err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleApprove = async (id) => {
    try {
      await supabase.from('saas_projects_1776800000001').update({ status: 'approved' }).eq('id', id);
      appCache.clearTools();
      fetchSubmissions();
    } catch (err) {
      console.error(err);
      alert('Failed to approve tool');
    }
  };

  const handleDecline = async (id) => {
    if (!confirm('Are you sure you want to decline this submission?')) return;
    try {
      await supabase.from('saas_projects_1776800000001').update({ status: 'rejected' }).eq('id', id);
      fetchSubmissions();
    } catch (err) {
      console.error(err);
      alert('Failed to decline tool');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this submission completely?')) return;
    try {
      await supabase.from('saas_projects_1776800000001').delete().eq('id', id);
      fetchSubmissions();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Pending Submissions</h2>
          <p className="text-white/40 text-sm">{submissions.length} tools awaiting review</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-20 text-white/30 border border-dashed border-[#392e4e] rounded-2xl">
          <SafeIcon icon={FiInbox} className="text-4xl mx-auto mb-3 opacity-30" />
          <p>No pending submissions to review right now.</p>
        </div>
      ) : (
        <div className="bg-[#0c0518] border border-[#392e4e] rounded-xl w-full overflow-hidden shadow-sm">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left whitespace-nowrap min-w-max">
              <thead>
                <tr className="border-b border-white/5 bg-white/5 text-white/40 text-xs uppercase tracking-wider">
                  <th className="p-4 font-medium">Tool Details</th>
                  <th className="p-4 font-medium">Submitter</th>
                  <th className="p-4 font-medium">Category</th>
                  <th className="p-4 font-medium text-right">Review Action</th>
                </tr>
              </thead>
              <tbody className="text-white text-sm divide-y divide-white/5">
                {submissions.map(t => (
                  <tr key={t.id} className="hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.name} className="w-10 h-10 rounded-md object-cover border border-[#392e4e] flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-sm font-bold flex-shrink-0">{t.name.charAt(0)}</div>
                        )}
                        <div>
                          <div className="font-bold flex items-center gap-2 text-base">
                            {t.name}
                          </div>
                          <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:underline">{t.url.replace(/^https?:\/\//, '')}</a>
                          <div className="text-[10px] text-white/40 max-w-xs truncate mt-0.5" title={t.description}>{t.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-white/80">{t.submitter_name || 'Unknown'}</div>
                      <div className="text-xs text-white/40">{t.submitter_email || '-'}</div>
                      <div className="text-[10px] text-white/30 mt-1">{new Date(t.dateAdded).toLocaleDateString()}</div>
                    </td>
                    <td className="p-4 text-white/60">{t.category}</td>
                    <td className="p-4 flex gap-2 justify-end items-center h-full pt-6">
                      <button onClick={() => handleApprove(t.id)} title="Approve" className="p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-all shadow-sm">
                        <SafeIcon icon={FiCheck} className="text-lg" />
                      </button>
                      <button onClick={() => handleDecline(t.id)} title="Decline" className="p-2 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 transition-all shadow-sm">
                        <SafeIcon icon={FiX} className="text-lg" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} title="Delete completely" className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all shadow-sm ml-2">
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ── Tools Tab (Directory Management) ───────────────────────────────────────────
const ToolsTab = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchTools = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('saas_projects_1776800000001').select('*').order('dateAdded', { ascending: false });
      if (error) throw error;

      const dbNames = new Set((data || []).map(t => t.name));
      const hasAnyStatic = staticProjects.some(p => dbNames.has(p.name));

      if (!hasAnyStatic) {
        const toInsert = staticProjects.map(p => ({
          name: p.name,
          description: p.description,
          url: p.url,
          category: p.category,
          dateAdded: p.dateAdded ? new Date(p.dateAdded).toISOString() : new Date().toISOString(),
          featured: p.featured || false,
          tags: p.tags || [],
          status: 'approved'
        }));
        
        await supabase.from('saas_projects_1776800000001').insert(toInsert);
        appCache.clearTools();
        
        const { data: newData } = await supabase.from('saas_projects_1776800000001').select('*').order('dateAdded', { ascending: false });
        setTools((newData || []).filter(t => t.status !== 'pending'));
      } else {
        setTools((data || []).filter(t => t.status !== 'pending'));
      }
    } catch(err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const toggleSelect = (id) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) newSelection.delete(id);
    else newSelection.add(id);
    setSelectedIds(newSelection);
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedIds.size} tools?`)) return;
    const validIds = Array.from(selectedIds).filter(id => typeof id === 'string');
    if (validIds.length > 0) {
      await supabase.from('saas_projects_1776800000001').delete().in('id', validIds);
    }
    setSelectedIds(new Set());
    appCache.clearTools();
    fetchTools();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this tool?')) return;
    await supabase.from('saas_projects_1776800000001').delete().eq('id', id);
    appCache.clearTools();
    fetchTools();
  };

  if (showForm) {
    return (
      <div className="w-full">
        <ToolFormView tool={editingTool} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); fetchTools(); }} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">SaaS Directory</h2>
          <p className="text-white/40 text-sm">{tools.length} project{tools.length !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 font-bold rounded-xl hover:bg-red-500/30 transition-all text-sm">
              <SafeIcon icon={FiTrash} /> Delete ({selectedIds.size})
            </button>
          )}
          <button onClick={() => { setEditingTool(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 transition-all text-sm">
            <SafeIcon icon={FiPlus} /> Add Tool
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : tools.length === 0 ? (
        <div className="text-center py-20 text-white/30 border border-dashed border-[#392e4e] rounded-2xl">
          <SafeIcon icon={FiGrid} className="text-4xl mx-auto mb-3 opacity-30" />
          <p>No tools in database. Add a new tool to get started!</p>
        </div>
      ) : (
        <div className="bg-[#0c0518] border border-[#392e4e] rounded-xl w-full overflow-hidden shadow-sm">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left whitespace-nowrap min-w-max">
              <thead>
                <tr className="border-b border-white/5 bg-white/5 text-white/40 text-xs uppercase tracking-wider">
                  <th className="p-4 w-10">
                    <input type="checkbox" onChange={(e) => setSelectedIds(e.target.checked ? new Set(tools.map(t => t.id)) : new Set())} checked={selectedIds.size === tools.length && tools.length > 0} className="w-4 h-4 accent-purple-500 rounded" />
                  </th>
                  <th className="p-4 font-medium">Project</th>
                  <th className="p-4 font-medium">Category</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Featured</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white text-sm divide-y divide-white/5">
                {tools.map(t => (
                  <tr key={t.id} className={`hover:bg-white/5 transition-colors ${selectedIds.has(t.id) ? 'bg-purple-500/10' : ''}`}>
                    <td className="p-4">
                      <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} className="w-4 h-4 accent-purple-500 rounded" />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.name} className="w-8 h-8 rounded-md object-cover border border-[#392e4e] flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-red-600 to-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{t.name.charAt(0)}</div>
                        )}
                        <div>
                          <div className="font-bold flex items-center gap-2">
                            {t.name}
                          </div>
                          <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:underline">{t.url.replace(/^https?:\/\//, '')}</a>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-white/60">{t.category}</td>
                    <td className="p-4">
                      {t.status === 'rejected' ? (
                        <span className="px-2 py-1 rounded bg-red-500/20 text-red-500 text-[10px] font-bold uppercase tracking-wider">Rejected</span>
                      ) : (
                        <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wider">Approved</span>
                      )}
                    </td>
                    <td className="p-4">
                      {t.featured ? <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-500 text-xs font-bold">YES</span> : <span className="text-white/20 text-xs">NO</span>}
                    </td>
                    <td className="p-4 flex gap-2 justify-end">
                      <button onClick={() => { setEditingTool(t); setShowForm(true); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                        <SafeIcon icon={FiEdit2} className="text-sm" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400/60 hover:text-red-400 transition-all">
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Settings Tab (Keep-Alive) ───────────────────────────────────────────
const SettingsTab = () => {
  const [pings, setPings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);

  const fetchPings = useCallback(async () => {
    try {
      const { data } = await supabase.from('keepalive_pings_1776800000001').select('*').order('created_at', { ascending: false }).limit(10);
      setPings(data || []);
    } catch(err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPings();
  }, [fetchPings]);

  const handleManualPing = async () => {
    setPinging(true);
    try {
      await supabase.from('keepalive_pings_1776800000001').insert([{ source: 'manual_admin' }]);
      fetchPings();
      alert('Ping successful!');
    } catch(err) {
      console.error(err);
      alert('Ping failed');
    } finally {
      setPinging(false);
    }
  };

  return (
    <div className="max-w-3xl w-full">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">System Settings</h2>
        <p className="text-white/40 text-sm">Manage database keep-alive and configuration</p>
      </div>

      <div className="bg-[#0c0518] border border-[#392e4e] rounded-2xl p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
          <div>
            <h3 className="font-bold text-white flex items-center gap-2"><SafeIcon icon={FiActivity} className="text-green-500" /> Supabase Anti-Sleep</h3>
            <p className="text-white/50 text-sm mt-1">The app automatically pings the database every 24 hours while open to prevent the free tier from pausing.</p>
          </div>
          <button onClick={handleManualPing} disabled={pinging} className="px-5 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 transition-all text-sm flex items-center justify-center gap-2 w-full sm:w-auto">
            <SafeIcon icon={FiRefreshCw} className={pinging ? 'animate-spin' : ''} /> Ping Now
          </button>
        </div>

        <div className="border-t border-white/5 pt-4 mt-4 w-full">
          <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Recent Pings</h4>
          {loading ? (
            <div className="text-white/30 text-sm">Loading...</div>
          ) : pings.length === 0 ? (
            <div className="text-white/30 text-sm">No pings recorded yet.</div>
          ) : (
            <div className="space-y-2">
              {pings.map(p => (
                <div key={p.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm p-3 sm:p-2 rounded bg-[#150a28] border border-[#392e4e] gap-1">
                  <span className="text-white/80"><span className="text-purple-400 font-mono">[{p.source}]</span> successful ping</span>
                  <span className="text-white/40 text-xs sm:text-sm">{format(new Date(p.created_at), 'MMM d, h:mm:ss a')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reviews');
  const [stats, setStats] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [messages, setMessages] = useState([]);
  const [messageFilter, setMessageFilter] = useState('all');
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [pendingToolsCount, setPendingToolsCount] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await supabase.from('click_events').select('project_name, created_at');
      const counts = {};
      (data || []).forEach(e => {
        counts[e.project_name] = (counts[e.project_name] || 0) + 1;
      });
      const processed = Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
      setStats(processed);
      setTotalClicks((data || []).length);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await supabase.from('contact_messages').select('*').order('created_at', { ascending: false });
      setMessages(data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try {
      const { count } = await supabase.from('saas_projects_1776800000001')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingToolsCount(count || 0);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/admin/login');
          return;
        }

        await Promise.all([
          fetchStats(),
          fetchMessages(),
          fetchPendingCount()
        ]);
        
      } catch {
        navigate('/admin/login');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) navigate('/admin/login');
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, fetchStats, fetchMessages, fetchPendingCount]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleMarkAsRead = async (id, status) => {
    const newStatus = status === 'unread' ? 'read' : 'unread';
    await supabase.from('contact_messages').update({ status: newStatus }).eq('id', id);
    fetchMessages();
  };

  const handleSaveComment = async (id) => {
    await supabase.from('contact_messages').update({ admin_comment: commentText }).eq('id', id);
    setEditingComment(null);
    setCommentText('');
    fetchMessages();
  };

  const filteredMessages = messages.filter(m => messageFilter === 'all' || m.status === messageFilter);

  const chartOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#150a28', borderColor: '#392e4e', textStyle: { color: '#fff' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'value', axisLine: { lineStyle: { color: '#ffffff30' } }, axisLabel: { color: '#ffffff60' }, splitLine: { lineStyle: { color: '#ffffff10' } } },
    yAxis: { type: 'category', data: stats.slice(0, 10).map(s => s.name).reverse(), axisLine: { lineStyle: { color: '#ffffff30' } }, axisLabel: { color: '#ffffff60', width: 100, overflow: 'truncate' } },
    series: [
      {
        name: 'Clicks',
        type: 'bar',
        data: stats.slice(0, 10).map(s => s.count).reverse(),
        itemStyle: {
          color: { type: 'linear', x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: '#9333ea' }, { offset: 1, color: '#4f46e5' }] },
          borderRadius: [0, 4, 4, 0]
        }
      }
    ],
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  const tabs = [
    { id: 'reviews', label: 'Reviews', icon: FiStar },
    { id: 'tools', label: 'Tools', icon: FiGrid },
    { id: 'submissions', label: 'Submissions', icon: FiInbox, dot: pendingToolsCount > 0 },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
    { id: 'messages', label: 'Messages', icon: FiMessageSquare, dot: messages.some(m => m.status === 'unread') },
    { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-screen w-full overflow-x-hidden sm:overflow-visible">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4 w-full">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Admin Dashboard</h1>
          <p className="text-white/40 text-sm">Manage everything efficiently</p>
        </div>
        
        {/* Scrollable Tabs Wrapper for Mobile */}
        <div className="flex items-center gap-3 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
          <div className="flex bg-[#150a28] border border-[#392e4e] rounded-xl p-1 gap-1 min-w-max">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === t.id ? 'bg-purple-600 text-white shadow-lg' : 'text-white/60 hover:text-white'
                }`}
              >
                <SafeIcon icon={t.icon} className="text-sm" />
                <span>{t.label}</span>
                {t.dot && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_#ef4444]" />}
              </button>
            ))}
          </div>
          <button
            onClick={handleLogout}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all text-sm"
          >
            <SafeIcon icon={FiLogOut} /><span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {activeTab === 'reviews' && <ReviewsTab />}
      {activeTab === 'tools' && <ToolsTab />}
      {activeTab === 'submissions' && <SubmissionsTab onCountChange={setPendingToolsCount} />}
      {activeTab === 'settings' && <SettingsTab />}
      
      {activeTab === 'analytics' && (
        <div className="w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8 w-full">
            {[
              { icon: FiMousePointer, color: 'purple', label: 'Total Clicks', value: totalClicks },
              { icon: FiActivity, color: 'blue', label: 'Active Projects', value: stats.length },
              { icon: FiBarChart2, color: 'green', label: 'Top Performer', value: stats[0]?.name || 'N/A' },
            ].map((s, i) => (
              <div key={i} className="bg-[#0c0518] border border-[#392e4e] rounded-2xl p-6 w-full">
                <div className="flex items-center gap-4 mb-2">
                  <div className={`p-3 bg-${s.color}-500/10 rounded-xl`}>
                    <SafeIcon icon={s.icon} className={`text-${s.color}-400 text-xl`} />
                  </div>
                  <div className="text-white/40 text-sm font-medium">{s.label}</div>
                </div>
                <div className="text-2xl font-bold text-white truncate">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
            <div className="bg-[#0c0518] border border-[#392e4e] rounded-2xl p-4 sm:p-6 w-full overflow-hidden">
              <h3 className="text-xl font-bold text-white mb-6">Top Clicked Projects</h3>
              <div className="w-full overflow-x-auto">
                <ReactECharts option={chartOption} style={{ height: '400px', minWidth: '400px' }} />
              </div>
            </div>
            
            <div className="bg-[#0c0518] border border-[#392e4e] rounded-2xl p-4 sm:p-6 w-full overflow-hidden">
              <h3 className="text-xl font-bold text-white mb-6">Detailed Report</h3>
              <div className="overflow-x-auto w-full">
                <table className="w-full text-left min-w-max">
                  <thead><tr className="border-b border-white/10"><th className="pb-3 text-white/40 font-medium text-sm">Project</th><th className="pb-3 text-white/40 font-medium text-sm text-right">Clicks</th><th className="pb-3 text-white/40 font-medium text-sm text-right">Share</th></tr></thead>
                  <tbody className="text-white">
                    {stats.map(s => (
                      <tr key={s.name} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                        <td className="py-3 text-sm font-medium">{s.name}</td>
                        <td className="py-3 text-sm text-right font-mono text-purple-400">{s.count}</td>
                        <td className="py-3 text-sm text-right text-white/40">{Math.round((s.count / totalClicks) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="space-y-6 w-full">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiFilter} className="text-white/40" />
              <select 
                value={messageFilter} 
                onChange={e => setMessageFilter(e.target.value)}
                className="bg-[#150a28] border border-[#392e4e] rounded-lg px-3 py-2 text-white outline-none focus:border-purple-500 text-sm"
              >
                <option value="all">All Messages</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
            <span className="text-white/40 text-sm">Showing {filteredMessages.length} messages</span>
          </div>

          <div className="grid gap-4 w-full">
            {filteredMessages.map(msg => (
              <div key={msg.id} className={`bg-[#0c0518] border ${msg.status === 'unread' ? 'border-purple-500/50' : 'border-[#392e4e]'} rounded-xl p-4 sm:p-6 transition-all w-full overflow-hidden`}>
                <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                  <div className="flex items-start gap-4 overflow-hidden">
                    <div className={`p-3 rounded-xl flex-shrink-0 ${msg.status === 'unread' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-white/40'}`}>
                      <SafeIcon icon={FiMessageSquare} className="text-xl" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-white text-lg truncate">{msg.site_name}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-white/60 text-sm truncate">
                        <span className="font-medium text-white/80 truncate">{msg.name}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="font-mono truncate">{msg.email}</span>
                      </div>
                      <div className="text-white/30 text-xs mt-1">{format(new Date(msg.created_at), 'PPP p')}</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleMarkAsRead(msg.id, msg.status)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-2 h-fit w-full md:w-auto ${msg.status === 'unread' ? 'bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'}`}
                  >
                    <SafeIcon icon={msg.status === 'unread' ? FiCheck : FiMail} />
                    {msg.status === 'unread' ? 'Mark Read' : 'Mark Unread'}
                  </button>
                </div>
                
                <div className="bg-[#150a28] rounded-lg p-4 mb-4 text-white/80 whitespace-pre-wrap word-break break-words border border-[#392e4e] text-sm">{msg.message}</div>
                
                <div className="border-t border-white/5 pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
                    <label className="text-xs font-bold text-white/40 uppercase tracking-wider">Admin Notes</label>
                    {editingComment !== msg.id && <button onClick={() => { setEditingComment(msg.id); setCommentText(msg.admin_comment || ''); }} className="text-xs text-purple-400 hover:text-purple-300 self-start sm:self-auto">{msg.admin_comment ? 'Edit Note' : 'Add Note'}</button>}
                  </div>
                  {editingComment === msg.id ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input 
                        value={commentText} 
                        onChange={e => setCommentText(e.target.value)}
                        className="flex-1 w-full bg-[#150a28] border border-[#392e4e] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                        placeholder="Add internal notes..."
                      />
                      <button onClick={() => handleSaveComment(msg.id)} className="px-3 py-2 bg-purple-600 w-full sm:w-auto rounded-lg text-white hover:bg-purple-500 transition-colors flex justify-center items-center">
                        <SafeIcon icon={FiSave} />
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-white/50 italic">{msg.admin_comment || 'No notes added.'}</p>
                  )}
                </div>
              </div>
            ))}
            {filteredMessages.length === 0 && (
              <div className="text-center py-20 text-white/30 w-full">
                <SafeIcon icon={FiMessageSquare} className="text-4xl mx-auto mb-4 opacity-50" />
                <p>No messages found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;